/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef } from 'react';

import Imgix from 'js/components/Imgix';
import { useRetracked } from 'js/lib/retracked';
import type { TrackingData } from 'js/lib/retracked';

import { Grid } from '@coursera/cds-core';

import { appendParms } from 'bundles/shared-articles/utils/socialEmbeds';

import _t from 'i18n!nls/articles';

type Asset = {
  title?: string | null;
  description?: string | null;
  url?: string | null;
  contentType?: string | null;
};

export type Props = {
  title?: string | null;
  externalEmbedVideoUrl?: string | null;
  video?: Asset | null;
  previewImage?: Asset | null;
  trackingData?: TrackingData | null;
};

const styles = {
  container: () => css`
    padding: 56.25% 0 0 0;
    position: relative;
    margin-top: 3rem;
    background-color: var(--cds-color-grey-975);
    overflow: hidden;
  `,
  media: () => css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  `,
  image: () => css`
    width: 100%;
    position: relative;
    transform: translate(-50%, -50%);
    top: 50%;
    left: 50%;
    opacity: 0.4;
  `,
};

/**
 * Renders a video embedder for an article which can either display an embedded video or an external video with a preview image.
 * @param {Object} props - The properties for the video embedder component.
 * @param {Object} props.video - The video details.
 * @param {string} props.title - The title of the video.
 * @param {string} props.externalEmbedVideoUrl - The URL of the external embedded video.
 * @param {Object} props.previewImage - The preview image details.
 * @param {Object} props.trackingData - The tracking data for the video.
 * @returns {React.Element|null} The rendered video embedder component, or null if required data is missing.
 */
const ArticleVideoEmbedder = (props: Props) => {
  const { video, title, externalEmbedVideoUrl, previewImage, trackingData } = props;
  let processedUrl;
  const retrackedTrack = useRetracked();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  if ((!video?.url && !externalEmbedVideoUrl) || !previewImage?.url) {
    return null;
  }

  const handleVideoPlay = () => {
    if (videoRef.current) {
      retrackedTrack({ trackingName: 'video', action: 'play', trackingData: { video: video?.title, ...trackingData } });
    }
  };

  if (externalEmbedVideoUrl) {
    processedUrl = appendParms(externalEmbedVideoUrl);
  }

  return (
    <Grid container css={styles.container} data-testid="article-video">
      <Grid item xs={12}>
        {previewImage?.url && (
          <div css={styles.media}>
            <Imgix
              css={styles.image}
              src={previewImage.url}
              alt={previewImage.description || _t('Preview video image')}
              maxWidth={750}
              maxHeight={450}
              imgParams={{ quality: 60 }}
            />
          </div>
        )}
        {processedUrl ? (
          <iframe
            frameBorder="0"
            allow="autoplay; fullscreen;"
            allowFullScreen
            src={processedUrl}
            css={styles.media}
            title={title || _t('Video')}
            data-testid="external-video"
            loading="lazy"
          />
        ) : (
          video?.url && (
            <React.Fragment>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                ref={videoRef}
                controls
                width="100%"
                controlsList="nodownload"
                css={styles.media}
                data-testid="contentful-video"
                onPlay={handleVideoPlay}
              >
                <source src={video.url} type={video.contentType || undefined} />
                {_t('The video is not compatible with your browser.')}
              </video>
            </React.Fragment>
          )
        )}
      </Grid>
    </Grid>
  );
};

export default ArticleVideoEmbedder;
